import { logger } from "../utils/logger";
import { Link } from 'react-router';
import { ArrowRight, Zap, Package, Clock, CheckCircle, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { useState, useEffect } from 'react';
import { homepageAPI } from '../utils/api';

interface BundleFeature {
  id: string;
  icon: string;
  text: string;
  order: number;
}

interface BundlePricingSection {
  id: string;
  badgeText: string;
  badgeIcon: string;
  headline: string;
  description: string;
  features: BundleFeature[];
  urgencyText: string;
  urgencyIcon: string;
  ctaText: string;
  ctaLink: string;
  socialProofNumber: string;
  socialProofText: string;
  visible: boolean;
  updatedAt: string;
}

const iconMap: { [key: string]: any } = {
  Zap, Package, Clock, CheckCircle, Sparkles,
};

// ============================================
// SKELETON
// ============================================

const BundleOfferSkeleton = () => (
  <section className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">
    <div className="max-w-6xl mx-auto">
      <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-gradient-to-r from-[#D74444] via-[#e85e5e] to-[#c13939] p-8 sm:p-10 lg:p-12">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-8">

          {/* Left content skeleton */}
          <div className="flex-1 w-full space-y-5">
            {/* Badge pill */}
            <div className="flex justify-center lg:justify-start">
              <div className="h-8 w-44 bg-white/20 rounded-full relative overflow-hidden">
                <div className="absolute inset-0 shimmer" />
              </div>
            </div>

            {/* Headline */}
            <div className="space-y-2">
              <div className="h-9 bg-white/20 rounded w-full relative overflow-hidden">
                <div className="absolute inset-0 shimmer" />
              </div>
              <div className="h-9 bg-white/20 rounded w-3/4 relative overflow-hidden">
                <div className="absolute inset-0 shimmer" />
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2 max-w-2xl">
              <div className="h-4 bg-white/20 rounded w-full relative overflow-hidden">
                <div className="absolute inset-0 shimmer" />
              </div>
              <div className="h-4 bg-white/20 rounded w-5/6 relative overflow-hidden">
                <div className="absolute inset-0 shimmer" />
              </div>
            </div>

            {/* Feature pills */}
            <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-9 w-36 bg-white/20 rounded-full relative overflow-hidden">
                  <div className="absolute inset-0 shimmer" />
                </div>
              ))}
            </div>

            {/* Urgency indicator */}
            <div className="h-7 w-48 bg-white/20 rounded-full relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
          </div>

          {/* Right CTA skeleton */}
          <div className="flex-shrink-0 flex flex-col items-center gap-3">
            <div className="h-14 w-56 bg-white/30 rounded-full relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
            <div className="h-4 w-40 bg-white/20 rounded relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
          </div>

        </div>
      </div>
    </div>
  </section>
);

// ============================================
// COMPONENT
// ============================================

export function BundleOfferCampaign() {
  const [data, setData] = useState<BundlePricingSection | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBundlePricingData();
  }, []);

  const fetchBundlePricingData = async () => {
    setLoading(true);
    try {
      const result = await homepageAPI.getBundlePricing();
      if (result.success && result.data) {
        setData(result.data);
      }
    } catch (error) {
      logger.error('❌ [BundleOfferCampaign] Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getIconComponent = (iconName: string) => iconMap[iconName] || CheckCircle;

  // Show skeleton while loading
  if (loading) {
    return <BundleOfferSkeleton />;
  }

  // Don't render if data not loaded or explicitly hidden
  if (!data || !data.visible) return null;

  const BadgeIcon = getIconComponent(data.badgeIcon);
  const UrgencyIcon = getIconComponent(data.urgencyIcon);

  return (
    <section className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">
      {/* Premium Matte Background with Gradient Overlays */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-gradient-to-br from-[#D74444] via-[#e85e5e] to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-gradient-to-tr from-[#E8B84D] via-[#E87D3E] to-transparent rounded-full blur-3xl" />
      </div>

      {/* Subtle Matte Texture Overlay */}
      <div className="absolute inset-0 opacity-[0.01]" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' /%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\' /%3E%3C/svg%3E")'}} />
      
      <div className="max-w-6xl mx-auto relative z-10">
        {/* Premium Gradient Card with Frosted Glass Effect */}
        <div className="relative rounded-3xl overflow-hidden shadow-2xl">
          <div className="absolute inset-0 bg-gradient-to-r from-[#D74444] via-[#e85e5e] to-[#c13939]" />
          <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-white/10" />
          <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px'}} />
          <div className="absolute -top-24 -right-24 w-64 h-64 bg-gradient-to-br from-[#E8B84D]/30 to-transparent rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-gradient-to-tr from-[#E87D3E]/30 to-transparent rounded-full blur-3xl" />
          
          <div className="relative p-8 sm:p-10 lg:p-12">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
              {/* Left Content */}
              <div className="flex-1 text-center lg:text-left">
                <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-xl px-4 py-2 rounded-full mb-5 border border-white/40 shadow-lg">
                  <div className="relative">
                    <BadgeIcon className="w-4 h-4 text-white fill-white" />
                    <div className="absolute inset-0 animate-ping">
                      <BadgeIcon className="w-4 h-4 text-white fill-white opacity-75" />
                    </div>
                  </div>
                  <span className="text-sm font-bold text-white">{data.badgeText}</span>
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                </div>
                
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 text-white leading-tight">
                  {data.headline}
                </h2>
                
                <p className="text-base sm:text-lg text-white/95 mb-6 max-w-2xl leading-relaxed">
                  {data.description}
                </p>

                {/* Feature Pills */}
                <div className="flex flex-wrap gap-3 mb-6 justify-center lg:justify-start">
                  {data.features.map((feature) => {
                    const FeatureIcon = getIconComponent(feature.icon);
                    return (
                      <div key={feature.id} className="flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full border border-white/20">
                        <FeatureIcon className="w-4 h-4 text-white" />
                        <span className="text-sm font-medium text-white">{feature.text}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Urgency Indicator */}
                <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/20">
                  <UrgencyIcon className="w-3 h-3 text-[#E8B84D]" />
                  <span className="text-xs font-medium text-white/90">{data.urgencyText}</span>
                </div>
              </div>
              
              {/* Right CTA */}
              <div className="flex-shrink-0">
                <Link to={data.ctaLink}>
                  <Button className="group relative overflow-hidden bg-white hover:bg-gray-50 text-[#D74444] rounded-full px-8 h-14 gap-3 shadow-2xl hover:shadow-white/20 hover:scale-105 transition-all duration-300 font-bold text-base border-2 border-white/40">
                    <div className="absolute inset-0 bg-gradient-to-r from-white via-[#E8B84D]/10 to-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    <span className="relative z-10 flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-[#E87D3E] to-[#E8B84D] rounded-full flex items-center justify-center shadow-lg">
                        <Package className="w-5 h-5 text-white" />
                      </div>
                      <span>{data.ctaText}</span>
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </Button>
                </Link>
                
                <div className="text-center mt-3">
                  <p className="text-xs text-white/80">
                    <span className="font-bold text-white">{data.socialProofNumber}</span> {data.socialProofText}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}